import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { BrandLogo } from '../components/BrandLogo';
import { UserAvatar } from '../components/UserAvatar';
import { useAuth } from '../auth/AuthContext';
import { isModuleEnabled } from '../lib/trackingPrefs';
import {
  AchievementBadge,
  PremiumCard,
  PremiumPage,
  PrimaryButton,
  SectionHeader,
  StatRing,
} from '../components/premium';
import { NavIcon } from '../components/NavIcons';
import { CircleDashboardCard } from '../components/circles/CircleDashboardCard';
import { AffiliatePlacement } from '../components/AffiliatePlacement';
import { api } from '../lib/api';
import { fetchCirclesDashboard } from '../lib/circles/api';
import type { CircleDashboard } from '../types/circles';
import { calorieTargetForDateKey } from '../lib/weeklyCalories';
import { localDateKey, localDateKeyFromIso } from '../lib/dateKey';
import { getTrainingPrefs, isScheduledRecoveryDay } from '../lib/trainingPrefs';
import { workoutDayStreakFromIso } from '../lib/workoutStreak';
import type { UserProfile, Workout } from '../types';

type DayOverview = {
  totals: { calories: number; proteinG: number };
  waterMl: number;
  sleepHours: number | null;
  meals?: unknown[];
};

const WATER_TARGET_ML = 2500;

const goalLabel: Record<string, string> = {
  cut: 'Cut',
  bulk: 'Bulk',
  maintenance: 'Maintain',
};

export function DashboardPage() {
  const { user } = useAuth();
  const showNutrition = isModuleEnabled(user, 'nutrition');
  const showWorkouts = isModuleEnabled(user, 'workouts');
  const showSleep = isModuleEnabled(user, 'sleep');
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [day, setDay] = useState<DayOverview | null>(null);
  const [workouts, setWorkouts] = useState<Workout[]>([]);
  const [circleDash, setCircleDash] = useState<CircleDashboard | null>(null);
  const dateKey = localDateKey();

  useEffect(() => {
    let c = false;
    (async () => {
      try {
        const [p, d, w] = await Promise.all([
          api<UserProfile | null>('/api/profile'),
          api<DayOverview>(`/api/nutrition/day/${dateKey}`),
          api<Workout[]>('/api/workouts'),
        ]);
        if (!c) {
          setProfile(p);
          setDay(d);
          setWorkouts(w);
        }
      } catch {
        if (!c) setProfile(null);
      }
    })();
    return () => {
      c = true;
    };
  }, [dateKey]);

  useEffect(() => {
    if (!user?.circlesEnabled) {
      setCircleDash(null);
      return;
    }
    let c = false;
    void fetchCirclesDashboard()
      .then((dash) => {
        if (!c) setCircleDash(dash);
      })
      .catch(() => {
        if (!c) setCircleDash(null);
      });
    return () => {
      c = true;
    };
  }, [user?.circlesEnabled]);

  const calorieTarget = calorieTargetForDateKey(profile, dateKey);
  const proteinTarget = profile?.proteinTargetG ?? 150;
  const cals = day?.totals.calories ?? 0;
  const prot = day?.totals.proteinG ?? 0;
  const waterMl = day?.waterMl ?? 0;
  const sleepH = day?.sleepHours ?? 0;
  const sleepTarget = 8;
  const mealCount = day?.meals?.length ?? 0;

  const workoutToday = useMemo(
    () => workouts.filter((w) => localDateKeyFromIso(w.startedAt) === dateKey),
    [workouts, dateKey],
  );
  const streak = workoutDayStreakFromIso(workouts.map((w) => w.startedAt));
  const recentWorkouts = workouts.slice(0, 3);
  const trainingPrefs = getTrainingPrefs(user?.id);
  const recoveryDay =
    showWorkouts && workoutToday.length === 0 && isScheduledRecoveryDay(false, trainingPrefs);

  const winsLeft = useMemo(() => {
    let n = 0;
    if (showWorkouts && workoutToday.length === 0 && !recoveryDay) n += 1;
    if (showNutrition && mealCount === 0) n += 1;
    if (showNutrition && prot < proteinTarget) n += 1;
    if (showSleep && sleepH <= 0) n += 1;
    return n;
  }, [showWorkouts, showNutrition, showSleep, workoutToday.length, mealCount, prot, proteinTarget, sleepH]);

  const wins = useMemo(() => {
    const items: { label: string; value: string; tone: 'gold' | 'success' | 'ai' }[] = [];
    if (showWorkouts && workoutToday.length > 0) items.push({ label: 'Lift', value: 'Logged', tone: 'success' });
    if (showNutrition && prot >= proteinTarget && proteinTarget > 0) items.push({ label: 'Protein', value: 'Hit', tone: 'gold' });
    if (streak >= 3) items.push({ label: 'Streak', value: `${streak}d`, tone: 'gold' });
    return items.slice(0, 3);
  }, [showWorkouts, showNutrition, workoutToday.length, prot, proteinTarget, streak]);

  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
  })();

  const dateLabel = new Date().toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
  });

  const primaryAction = (() => {
    if (showWorkouts && workoutToday.length === 0 && !recoveryDay) {
      return { label: "Start today's lift", to: '/workouts/session' };
    }
    if (showWorkouts && recoveryDay) {
      return { label: 'Recovery focus', to: '/workouts' };
    }
    if (showNutrition && mealCount === 0) {
      return { label: 'Log first meal', to: '/nutrition' };
    }
    if (showSleep && sleepH <= 0) {
      return { label: 'Add sleep', to: '/sleep' };
    }
    return { label: 'Review today', to: '/day-review' };
  })();

  return (
    <PremiumPage>
      <div className="mb-1 flex items-center justify-between gap-3">
        <BrandLogo heightClass="h-9 w-9" />
        <UserAvatar />
      </div>

      <PremiumCard variant="hero" className="!p-5">
        <p className="text-[11px] font-bold uppercase tracking-widest text-[var(--text-muted)]">{dateLabel}</p>
        <h1 className="mt-1 font-display text-[1.85rem] font-bold leading-tight text-[var(--text-primary)] sm:text-[2.1rem]">
          {greeting}
          {user?.displayName ? `, ${user.displayName.split(' ')[0]}` : ''}
        </h1>
        {profile?.fitnessGoal ? (
          <p className="mt-1 text-sm font-semibold text-accent">Goal: {goalLabel[profile.fitnessGoal] ?? profile.fitnessGoal}</p>
        ) : null}
        <div className="mt-3 inline-flex items-center gap-2 rounded-2xl border border-accent/25 bg-accent/10 px-4 py-2">
          <span className="font-display text-2xl font-bold tabular-nums text-accent">{winsLeft}</span>
          <span className="text-sm font-semibold text-[var(--text-secondary)]">
            {winsLeft === 1 ? 'win left today' : 'wins left today'}
          </span>
        </div>
        {winsLeft === 0 ? (
          <p className="mt-2 text-sm font-medium text-emerald-400">All targets hit — nice work</p>
        ) : null}
        <div className="mt-3 flex flex-wrap gap-2">
          {showNutrition ? (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/20 bg-emerald-500/10 px-3 py-1 text-xs font-semibold text-emerald-200">
              <NavIcon name="nutrition" className="h-3.5 w-3.5" />
              {cals} kcal
            </span>
          ) : null}
          {showNutrition ? (
            <span className="inline-flex rounded-full border border-emerald-500/15 bg-emerald-500/8 px-3 py-1 text-xs font-semibold text-emerald-100/90">
              {Math.round(prot)}g protein
            </span>
          ) : null}
          {showWorkouts ? (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-sky-500/20 bg-sky-500/10 px-3 py-1 text-xs font-semibold text-sky-200">
              <NavIcon name="workouts" className="h-3.5 w-3.5" />
              {workoutToday.length ? 'Lift done' : 'No lift'}
            </span>
          ) : null}
          {showSleep ? (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-indigo-500/20 bg-indigo-500/10 px-3 py-1 text-xs font-semibold text-indigo-200">
              <NavIcon name="sleep" className="h-3.5 w-3.5" />
              {sleepH > 0 ? `${sleepH}h` : 'Sleep'}
            </span>
          ) : null}
        </div>
        {streak > 0 && showWorkouts ? (
          <div className="mt-4 inline-flex rounded-full border border-amber-400/25 bg-amber-500/10 px-3 py-1 text-xs font-bold text-amber-300">
            {streak}-day streak
          </div>
        ) : null}
      </PremiumCard>

      {circleDash?.enabled ? <CircleDashboardCard data={circleDash} /> : null}

      {showNutrition || showWorkouts ? <AffiliatePlacement slot="inline" /> : null}

      <SectionHeader title="Today" />
      <div className="grid grid-cols-2 gap-3 auto-rows-fr">
        {showNutrition ? (
          <Link to="/nutrition" className="min-w-0">
            <PremiumCard variant="nutrition" className="flex h-full min-h-[148px] flex-col items-center justify-center !p-4">
              <StatRing value={cals} max={calorieTarget} label="Calories" size={72} />
              <p className="mt-2 text-center text-xs text-[var(--text-muted)]">
                {cals} / {calorieTarget}
              </p>
              <p className="text-center text-[11px] font-semibold text-emerald-400">{Math.max(0, calorieTarget - cals)} left</p>
            </PremiumCard>
          </Link>
        ) : null}
        {showNutrition ? (
          <Link to="/nutrition" className="min-w-0">
            <PremiumCard variant="nutrition" className="flex h-full min-h-[148px] flex-col justify-center !p-4">
              <div className="text-[10px] font-bold uppercase tracking-wide text-emerald-300/80">Protein</div>
              <div className="mt-1 font-display text-2xl font-bold text-[var(--text-primary)]">{Math.round(prot)}g</div>
              <div className="mt-2 h-2 overflow-hidden rounded-full bg-white/10">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-cyan-400"
                  style={{ width: `${proteinTarget > 0 ? Math.min(100, (prot / proteinTarget) * 100) : 0}%` }}
                />
              </div>
              <p className="mt-1.5 text-[11px] text-[var(--text-muted)]">
                {Math.round(prot)} / {proteinTarget}g · {Math.max(0, proteinTarget - prot)}g left
              </p>
            </PremiumCard>
          </Link>
        ) : null}
        {showNutrition ? (
          <Link to="/nutrition?focus=water" className="min-w-0">
            <PremiumCard variant="nutrition" className="flex h-full min-h-[148px] flex-col justify-between !p-4">
              <div className="text-[10px] font-bold uppercase tracking-wide text-cyan-300/80">Water</div>
              <div className="font-display text-xl font-bold text-[var(--text-primary)]">
                {Math.round(waterMl / 100) / 10}L
              </div>
              <div className="flex h-16 flex-col justify-end overflow-hidden rounded-xl bg-white/5">
                <div
                  className="w-full rounded-t-xl bg-gradient-to-t from-cyan-500/70 to-cyan-400/40"
                  style={{ height: `${Math.min(100, (waterMl / WATER_TARGET_ML) * 100)}%` }}
                />
              </div>
              <p className="text-[11px] text-[var(--text-muted)]">/ {WATER_TARGET_ML / 1000}L</p>
            </PremiumCard>
          </Link>
        ) : null}
        {showWorkouts ? (
          <Link to={recoveryDay ? '/workouts' : '/workouts/session'} className="min-w-0">
            <PremiumCard variant={recoveryDay ? 'sleep' : 'train'} className="flex h-full min-h-[148px] flex-col justify-between !p-4">
              <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wide text-sky-300/80">
                <NavIcon name={recoveryDay ? 'sleep' : 'workouts'} className="h-3.5 w-3.5" />
                {recoveryDay ? 'Recovery' : 'Workout'}
              </div>
              <div className="font-display text-lg font-bold text-[var(--text-primary)]">
                {workoutToday.length ? 'Complete' : recoveryDay ? 'Rest day' : 'Start workout'}
              </div>
              <span className={`text-xs font-semibold ${recoveryDay ? 'text-indigo-300' : 'text-sky-400'}`}>
                {workoutToday.length ? 'View' : recoveryDay ? 'Focus →' : 'Start →'}
              </span>
            </PremiumCard>
          </Link>
        ) : null}
        {showSleep ? (
          <Link to="/sleep" className={`min-w-0 ${!showNutrition && !showWorkouts ? 'col-span-2' : ''}`}>
            <PremiumCard variant="sleep" className="flex h-full min-h-[148px] flex-col justify-between !p-4">
              <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wide text-indigo-300/80">
                <NavIcon name="sleep" className="h-3.5 w-3.5" />
                Sleep
              </div>
              <div className="font-display text-lg font-bold text-[var(--text-primary)]">{sleepH > 0 ? `${sleepH}h` : 'Not logged'}</div>
              <span className="text-xs font-semibold text-indigo-300">{sleepH > 0 ? 'View' : 'Add'} →</span>
            </PremiumCard>
          </Link>
        ) : null}
      </div>

      <Link to={primaryAction.to} className="block">
        <PrimaryButton fullWidth className="!min-h-[56px] !text-base">
          {primaryAction.label}
        </PrimaryButton>
      </Link>

      {wins.length > 0 ? (
        <>
          <SectionHeader title="Wins" />
          <div className="grid grid-cols-3 gap-2">
            {wins.map((w) => (
              <AchievementBadge key={w.label} label={w.label} value={w.value} tone={w.tone} />
            ))}
          </div>
        </>
      ) : null}

      {showWorkouts && recentWorkouts.length > 0 ? (
        <>
          <SectionHeader title="Activity" />
          <ul className="space-y-2">
            {recentWorkouts.map((w) => (
              <li key={w.id}>
                <PremiumCard className="!p-3.5">
                  <div className="flex items-center gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/[0.06] text-accent">
                      <NavIcon name="workouts" className="h-4 w-4" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-bold text-[var(--text-primary)]">{w.name}</div>
                      <div className="text-xs text-[var(--text-muted)]">
                        {new Date(w.startedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                        {w.estimatedCaloriesBurned ? ` · ${w.estimatedCaloriesBurned} kcal` : ''}
                      </div>
                    </div>
                  </div>
                </PremiumCard>
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </PremiumPage>
  );
}
